import React, { useEffect, useRef } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Add as ZoomInIcon, Close as CloseIcon, Remove as ZoomOutIcon, Satellite as ImageryIcon, Map as StreetsIcon } from '@mui/icons-material';
import Map, { Layer, Marker, ScaleControl, Source } from 'react-map-gl';
import type { MapRef } from 'react-map-gl';
import { SEQUENCE_BADGE_RING_R, leaderStart } from './CameraCallouts';

/** Imagery basemap offered inside insets regardless of the main map's style. */
export const INSET_IMAGERY_STYLE = 'mapbox://styles/mapbox/satellite-streets-v12';

/**
 * Dwell insets: small zoomed-in map "windows" pinned next to a dwell on the
 * main map, with a dashed leader back to the dwell. Built for the analyst who
 * needs one screenshot that shows the overall pattern plus close-ups of the
 * places that matter.
 *
 * Each inset is a real (non-interactive) Mapbox map so it matches the base
 * style exactly. An inset is only mounted after the user asks for it, so no
 * tiles are fetched for dwells nobody has selected.
 */
export type DwellInset = {
  key: string;
  lon: number;
  lat: number;
  /** Place name, or coordinates when none. */
  title: string;
  subtitle?: string;
  /** Card top-left relative to the dwell point, in px. */
  dx: number;
  dy: number;
  /** Map viewport size in px (footer is added below). */
  w?: number;
  h?: number;
  zoom?: number;
  /** Show satellite imagery in this inset instead of the main map's style. */
  imagery?: boolean;
};

export const INSET_DEFAULT_W = 220;
export const INSET_DEFAULT_H = 170;
export const INSET_MIN_W = 140;
export const INSET_MIN_H = 100;
export const INSET_MAX_W = 640;
export const INSET_MAX_H = 520;
export const INSET_DEFAULT_ZOOM = 16.5;
export const INSET_MIN_ZOOM = 11;
export const INSET_MAX_ZOOM = 20;
const FOOTER_H = 32;
const DWELL_COLOR = '#ff0000';

export function insetSize(inset: Pick<DwellInset, 'w' | 'h'>): { w: number; mapH: number; h: number } {
  const w = Math.min(INSET_MAX_W, Math.max(INSET_MIN_W, inset.w ?? INSET_DEFAULT_W));
  const mapH = Math.min(INSET_MAX_H, Math.max(INSET_MIN_H, inset.h ?? INSET_DEFAULT_H));
  return { w, mapH, h: mapH + FOOTER_H };
}

export function dwellInsetKey(id: number | string, lon: number, lat: number): string {
  return `dwell-${id}@${lon.toFixed(6)},${lat.toFixed(6)}`;
}

/** Default placement cycles through quadrants so successive insets don't stack. */
export function defaultInsetOffset(index: number): { dx: number; dy: number } {
  const gap = 80;
  const { w, h } = insetSize({});
  const quadrants = [
    { dx: gap, dy: -h - 30 }, // up-right
    { dx: -w - gap, dy: -h - 30 }, // up-left
    { dx: gap, dy: 40 }, // down-right
    { dx: -w - gap, dy: 40 }, // down-left
  ];
  const q = quadrants[index % quadrants.length];
  // Each lap around the quadrants nudges outward so a 5th+ inset stays visible.
  const lap = Math.floor(index / quadrants.length) * 40;
  return { dx: q.dx + (q.dx > 0 ? lap : -lap), dy: q.dy + (q.dy > 0 ? lap : -lap) };
}

/** Point on the card border nearest the dwell; null when the dwell sits under the card. */
function connectorEnd(dx: number, dy: number, w: number, h: number): { x: number; y: number } | null {
  const inside = dx <= 0 && 0 <= dx + w && dy <= 0 && 0 <= dy + h;
  if (inside) return null;
  return {
    x: Math.min(Math.max(0, dx), dx + w),
    y: Math.min(Math.max(0, dy), dy + h),
  };
}

type CardProps = {
  inset: DwellInset;
  /** Dwell point in map-container pixels. */
  x: number;
  y: number;
  mapStyle: string;
  onOffsetChange: (key: string, dx: number, dy: number) => void;
  onResize: (key: string, w: number, h: number) => void;
  onZoom: (key: string, zoom: number) => void;
  onToggleImagery: (key: string) => void;
  onRemove: (key: string) => void;
};

const InsetCard: React.FC<CardProps> = ({ inset, x, y, mapStyle, onOffsetChange, onResize, onZoom, onToggleImagery, onRemove }) => {
  const { key, dx, dy } = inset;
  const { w, mapH, h } = insetSize(inset);
  const zoom = inset.zoom ?? INSET_DEFAULT_ZOOM;
  const dragRef = useRef<{ startX: number; startY: number; dx: number; dy: number; moved: boolean } | null>(null);
  const resizeRef = useRef<{ startX: number; startY: number; w: number; h: number } | null>(null);
  const insetMapRef = useRef<MapRef | null>(null);

  // Keep the dwell dead-centre through resizes: tell mapbox the canvas size
  // changed and re-centre on the next frame (the container's ResizeObserver
  // alone leaves the old viewport anchored top-left).
  useEffect(() => {
    const m = insetMapRef.current?.getMap?.();
    if (!m) return;
    const id = requestAnimationFrame(() => {
      m.resize();
      m.jumpTo({ center: [inset.lon, inset.lat], zoom });
    });
    return () => cancelAnimationFrame(id);
  }, [w, mapH, inset.lon, inset.lat, zoom]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, dx, dy, moved: false };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const mx = e.clientX - d.startX;
    const my = e.clientY - d.startY;
    if (!d.moved && Math.hypot(mx, my) < 4) return;
    d.moved = true;
    onOffsetChange(key, d.dx + mx, d.dy + my);
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    dragRef.current = null;
  };

  const onResizeDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    resizeRef.current = { startX: e.clientX, startY: e.clientY, w, h: mapH };
  };
  const onResizeMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = resizeRef.current;
    if (!r) return;
    onResize(key, r.w + (e.clientX - r.startX), r.h + (e.clientY - r.startY));
  };
  const onResizeUp = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    resizeRef.current = null;
  };

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <Box sx={{ position: 'absolute', left: x, top: y, width: 0, height: 0, pointerEvents: 'none' }}>
      <Box
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={stop}
        onWheel={stop}
        sx={{
          position: 'absolute',
          left: dx,
          top: dy,
          width: w,
          height: h,
          pointerEvents: 'auto',
          cursor: 'move',
          touchAction: 'none',
          bgcolor: '#fff',
          color: '#111',
          border: '2px solid #fff',
          outline: '1px solid rgba(0,0,0,0.6)',
          borderRadius: '3px',
          boxShadow: '0 6px 18px rgba(0,0,0,0.45)',
          userSelect: 'none',
          '&:hover .inset-ctl': { opacity: 1 },
        }}
      >
        {/* The inset map never takes pointer events; the card handles drag. */}
        <Box
          sx={{
            height: mapH,
            position: 'relative',
            overflow: 'hidden',
            pointerEvents: 'none',
            // Compact scale bar tucked into the corner.
            '& .mapboxgl-ctrl-top-left .mapboxgl-ctrl': { margin: '6px 0 0 6px' },
            '& .mapboxgl-ctrl-scale': { fontSize: '9px', lineHeight: '12px', padding: '0 4px', height: 'auto' },
            '& .mapboxgl-ctrl-bottom-right .mapboxgl-ctrl': { margin: '0 6px 6px 0' },
          }}
        >
          <Map
            ref={insetMapRef}
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
            mapStyle={inset.imagery ? INSET_IMAGERY_STYLE : mapStyle}
            longitude={inset.lon}
            latitude={inset.lat}
            zoom={zoom}
            interactive={false}
            preserveDrawingBuffer
            style={{ width: '100%', height: '100%' }}
          >
            <Source
              id="inset-dwell"
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [inset.lon, inset.lat] } }],
              }}
            >
              {/* Same look as the main map's dwell circles so the inset reads as a close-up. */}
              <Layer
                id="inset-dwell-circle"
                type="circle"
                paint={{
                  'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 5, 12, 15, 16, 30, 20, 50],
                  'circle-color': DWELL_COLOR,
                  'circle-opacity': 0.3,
                  'circle-stroke-color': DWELL_COLOR,
                  'circle-stroke-width': 2,
                  'circle-stroke-opacity': 0.8,
                }}
              />
            </Source>
            <Marker longitude={inset.lon} latitude={inset.lat} anchor="center">
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: DWELL_COLOR, border: '2px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,0.5)' }} />
            </Marker>
            <ScaleControl position="top-left" maxWidth={Math.min(80, Math.round(w * 0.35))} />
          </Map>
          {/* Zoom nudge + close, shown on hover. */}
          <Box
            className="inset-ctl"
            sx={{ position: 'absolute', top: 2, right: 2, display: 'flex', gap: '2px', opacity: 0, transition: 'opacity 120ms', pointerEvents: 'auto' }}
          >
            <Tooltip title="Zoom in" placement="top">
              <span>
                <IconButton
                  size="small"
                  aria-label="Zoom inset in"
                  disabled={zoom >= INSET_MAX_ZOOM}
                  onPointerDown={stop}
                  onClick={(e) => {
                    e.stopPropagation();
                    onZoom(key, Math.min(INSET_MAX_ZOOM, zoom + 1));
                  }}
                  sx={ctlSx}
                >
                  <ZoomInIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Zoom out" placement="top">
              <span>
                <IconButton
                  size="small"
                  aria-label="Zoom inset out"
                  disabled={zoom <= INSET_MIN_ZOOM}
                  onPointerDown={stop}
                  onClick={(e) => {
                    e.stopPropagation();
                    onZoom(key, Math.max(INSET_MIN_ZOOM, zoom - 1));
                  }}
                  sx={ctlSx}
                >
                  <ZoomOutIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={inset.imagery ? 'Switch to map' : 'Switch to imagery'} placement="top">
              <IconButton
                size="small"
                aria-label={inset.imagery ? 'Switch inset to map basemap' : 'Switch inset to imagery basemap'}
                aria-pressed={Boolean(inset.imagery)}
                onPointerDown={stop}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleImagery(key);
                }}
                sx={ctlSx}
              >
                {inset.imagery ? <StreetsIcon sx={{ fontSize: 12 }} /> : <ImageryIcon sx={{ fontSize: 12 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove inset" placement="top">
              <IconButton
                size="small"
                aria-label="Remove inset"
                onPointerDown={stop}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(key);
                }}
                sx={ctlSx}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 0.75, height: FOOTER_H }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: DWELL_COLOR, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{ fontSize: '0.68rem', fontWeight: 700, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              title={inset.title}
            >
              {inset.title}
            </Box>
            {inset.subtitle && (
              <Box
                sx={{ fontSize: '0.6rem', color: '#555', lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                title={inset.subtitle}
              >
                {inset.subtitle}
              </Box>
            )}
          </Box>
        </Box>
        {/* Resize grip (bottom-right), shown on hover. */}
        <Box
          className="inset-ctl"
          role="separator"
          aria-label="Resize inset"
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          onPointerCancel={onResizeUp}
          sx={{
            position: 'absolute',
            right: -1,
            bottom: -1,
            width: 14,
            height: 14,
            cursor: 'nwse-resize',
            touchAction: 'none',
            opacity: 0,
            transition: 'opacity 120ms',
            borderBottomRightRadius: '3px',
            background:
              'linear-gradient(135deg, transparent 0 50%, rgba(0,0,0,0.45) 50% 60%, transparent 60% 70%, rgba(0,0,0,0.45) 70% 80%, transparent 80% 90%, rgba(0,0,0,0.45) 90%)',
          }}
        />
      </Box>
    </Box>
  );
};

const ctlSx = {
  p: '2px',
  bgcolor: 'rgba(0,0,0,0.6)',
  color: '#fff',
  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
  '&.Mui-disabled': { bgcolor: 'rgba(0,0,0,0.35)', color: 'rgba(255,255,255,0.5)' },
} as const;

export type DwellInsetsProps = {
  insets: DwellInset[];
  mapRef: React.RefObject<MapRef | null>;
  /** Base style URL of the main map so insets match it. */
  mapStyle: string;
  /** 'ring' leaves a route-sequence number under the anchor readable. */
  anchor?: 'dot' | 'ring';
  onOffsetChange: (key: string, dx: number, dy: number) => void;
  onResize: (key: string, w: number, h: number) => void;
  onZoom: (key: string, zoom: number) => void;
  onToggleImagery: (key: string) => void;
  onRemove: (key: string) => void;
};

/**
 * Render as a SIBLING of the main <Map> inside the same relatively-positioned
 * container (same pattern as CameraCallouts).
 */
const DwellInsets: React.FC<DwellInsetsProps> = ({
  insets,
  mapRef,
  mapStyle,
  anchor = 'dot',
  onOffsetChange,
  onResize,
  onZoom,
  onToggleImagery,
  onRemove,
}) => {
  // Re-projected each render; the parent re-renders on every map move.
  const map = mapRef.current?.getMap?.();
  const positioned = map
    ? insets.map((inset) => {
        const p = map.project([inset.lon, inset.lat]);
        return { inset, x: p.x, y: p.y };
      })
    : [];

  if (positioned.length === 0) return null;
  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 2 }} data-dwell-insets="">
      <svg
        width="100%"
        height="100%"
        style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', overflow: 'visible' }}
        aria-hidden
      >
        {positioned.map(({ inset, x, y }) => {
          const { w, h } = insetSize(inset);
          const end = connectorEnd(inset.dx, inset.dy, w, h);
          const ring = anchor === 'ring';
          const start = end ? (ring ? leaderStart(x, y, x + end.x, y + end.y, SEQUENCE_BADGE_RING_R) : { x, y }) : null;
          return (
            <g key={inset.key}>
              {end && start && (
                <>
                  <line x1={start.x} y1={start.y} x2={x + end.x} y2={y + end.y} stroke="rgba(0,0,0,0.7)" strokeWidth={3.5} strokeLinecap="round" />
                  <line x1={start.x} y1={start.y} x2={x + end.x} y2={y + end.y} stroke="#fff" strokeWidth={1.5} strokeDasharray="6 4" strokeLinecap="round" />
                  <circle cx={x + end.x} cy={y + end.y} r={2.5} fill="#fff" stroke="rgba(0,0,0,0.7)" strokeWidth={1} />
                </>
              )}
              {ring ? (
                <circle cx={x} cy={y} r={SEQUENCE_BADGE_RING_R} fill="none" stroke="#fff" strokeWidth={1.5} style={{ filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.8))' }} />
              ) : (
                <circle cx={x} cy={y} r={4.5} fill={DWELL_COLOR} stroke="#fff" strokeWidth={1.5} />
              )}
            </g>
          );
        })}
      </svg>
      {positioned.map(({ inset, x, y }) => (
        <InsetCard
          key={inset.key}
          inset={inset}
          x={x}
          y={y}
          mapStyle={mapStyle}
          onOffsetChange={onOffsetChange}
          onResize={onResize}
          onZoom={onZoom}
          onToggleImagery={onToggleImagery}
          onRemove={onRemove}
        />
      ))}
    </Box>
  );
};

export default DwellInsets;
